import { getOtpFunnelInstanceId, resolveOtokocDeviceContext } from './deviceMetadata.js';

let bookingOtpApiBase = `${window.location.origin}/api`;

export const BOOKING_OTP_CODE_LENGTH = 5;

export type BookingOtpResponse = {
  success: boolean;
  statusCode?: string;
  statusDescription?: string;
  networkError?: string;
  transportError?: boolean;
  verificationReceipt?: string;
  otpLeadId?: string;
};

interface SendBookingOtpOptions {
  actionId?: string;
  threadId?: string;
  bookingCycleId?: number | null;
  source?: 'booking' | 'voice' | 'text-lead';
}

interface VerifyBookingOtpOptions {
  actionId?: string;
  otpLeadId?: string;
  threadId?: string;
  bookingCycleId?: number | null;
  source?: 'booking' | 'voice' | 'text-lead';
}

export function setBookingOtpApiBase(nextBaseUrl: string): void {
  bookingOtpApiBase = nextBaseUrl.replace(/\/$/, '');
}

async function postBookingOtp(
  path: string,
  body: Record<string, unknown>
): Promise<BookingOtpResponse> {
  try {
    const response = await fetch(`${bookingOtpApiBase}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      mode: 'cors',
    });
    const data = (await response.json()) as BookingOtpResponse;

    if (!response.ok) {
      return {
        success: false,
        statusCode: data.statusCode ?? '',
        statusDescription: data.statusDescription || `HTTP ${response.status}`,
        networkError: `HTTP ${response.status}`,
      };
    }

    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    return {
      success: false,
      statusCode: '',
      statusDescription: 'İstek gönderilemedi',
      networkError: message,
      transportError: true,
    };
  }
}

export function normalizeBookingPhone(raw: string): string | null {
  const compact = raw.replace(/[^\d]/g, '');

  if (compact.length === 10 && compact.startsWith('5')) {
    return `0${compact}`;
  }

  if (compact.length === 11 && compact.startsWith('05')) {
    return compact;
  }

  if (compact.length === 12 && compact.startsWith('90')) {
    return `0${compact.slice(2)}`;
  }

  return null;
}

export function formatBookingPhone(raw: string): string {
  const normalized = normalizeBookingPhone(raw);

  if (!normalized) {
    return raw;
  }

  return normalized.replace(/(\d{4})(\d{3})(\d{2})(\d{2})/, '$1 $2 $3 $4');
}

export function formatOtpSendError(raw: string): string {
  const text = raw.toLowerCase();

  if (text.includes('cors') || text.includes('failed to fetch') || text.includes('network')) {
    return 'Şu an SMS gönderilemiyor. Lütfen bağlantınızı kontrol edip tekrar deneyin.';
  }

  return raw.length > 180 ? 'SMS gönderilemedi. Lütfen birkaç dakika sonra tekrar deneyin.' : raw;
}

export function formatOtpVerifyError(raw: string): string {
  const text = raw.toLowerCase();

  if (
    raw.includes('Object reference') ||
    text.includes('nullreference') ||
    text.includes('hatalı içerik') ||
    text.includes('hatali icerik')
  ) {
    return 'Girdiğiniz kod geçersiz veya süresi dolmuş olabilir. SMS’teki kodu kontrol edip tekrar deneyin.';
  }

  if (text.includes('cors') || text.includes('failed to fetch') || text.includes('network')) {
    return 'Şu an doğrulama yapılamıyor. Lütfen bağlantınızı kontrol edip tekrar deneyin.';
  }

  return raw.length > 180 ? 'Kod doğrulanamadı. Lütfen tekrar deneyin.' : raw;
}

export async function sendBookingOtpSms(
  gsmNos: string,
  options: SendBookingOtpOptions = {}
): Promise<BookingOtpResponse> {
  return postBookingOtp('/otp/send', {
    gsmNos,
    actionId: options.actionId,
    threadId: options.threadId || readBookingOtpThreadId(),
    otpFunnelInstanceId: getOtpFunnelInstanceId(),
    bookingCycleId:
      typeof options.bookingCycleId === 'number' && Number.isFinite(options.bookingCycleId)
        ? Math.trunc(options.bookingCycleId)
        : undefined,
    source: options.source,
    ...resolveOtokocDeviceContext(),
  });
}

export async function verifyBookingOtpCode(
  gsmNs: string,
  otpCode: string,
  options: VerifyBookingOtpOptions = {}
): Promise<BookingOtpResponse> {
  return postBookingOtp('/otp/check', {
    gsmNs,
    otpCode,
    actionId: options.actionId,
    otpLeadId: options.otpLeadId,
    threadId: options.threadId || readBookingOtpThreadId(),
    otpFunnelInstanceId: getOtpFunnelInstanceId(),
    bookingCycleId:
      typeof options.bookingCycleId === 'number' && Number.isFinite(options.bookingCycleId)
        ? Math.trunc(options.bookingCycleId)
        : undefined,
    source: options.source,
    ...resolveOtokocDeviceContext(),
  });
}

function readBookingOtpThreadId(): string | undefined {
  try {
    const stored = window.sessionStorage.getItem('gengage_session_id');
    return stored?.trim() || window.__gengageSessionId?.trim() || undefined;
  } catch {
    return window.__gengageSessionId?.trim() || undefined;
  }
}
